/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  HalfContetntLayout,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import CustomGrid from 'components/grids/CustomGrid';
import { ComboBox } from 'components/selects/ComboBox';
import { PrjScheduleBox } from 'models/setup/PrjSchedule';
import { getProjectBoxCnt, getSetupBoxList } from 'apis/setup/excp/box';
import { ItemList } from 'models/setup/excp/excp';
import { findPrjTargetEquipmentBox } from 'apis/setup/PrjSchedule';

type Props = {
  open: boolean;
  close: () => void;
  elmPrjId: string;
  tgtEqpId: string;
  selectedData?: PrjScheduleBox[];
  callbackFn: (selectedList: PrjScheduleBox[]) => void;
};

/**
 * 반입 대상 Box 선택 팝업
 */
const PrjScheduleBoxPopup = ({
  open,
  close,
  elmPrjId,
  tgtEqpId,
  selectedData = [],
  callbackFn,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const gridRef = useRef<any>();
  const selectedGridRef = useRef<any>();
  const [condition, setCondition] = useState<PrjScheduleBox>({ elmPrjId, tgtEqpId });
  const [checkedItems, setCheckedItems] = useState<PrjScheduleBox[]>(selectedData);
  const [rowData, setRowData] = useState<any>([]);

  const layoutDefinition = [
    {
      header: String(t('com.label.Box No', 'Box No')),
      binding: 'pakBoxNo',
      align: 'left',
      width: 120,
    },
    {
      binding: 'pakItemNm',
      header: String(t('com.label.품목 목록명', '품목 목록명')),
      align: 'left',
      width: '*',
    },
    {
      binding: 'bcdYn',
      header: String(t('com.label.바코드 여부', '바코드 여부')),
      align: 'center',
      width: 100,
    },
    {
      binding: 'boxStatNm',
      header: String(t('com.label.상태', '상태')),
      align: 'left',
      width: 100,
    },
  ];

  const initCodes = async () => {
    if (elmPrjId && tgtEqpId) {
      handleSearch();
    }
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => {
    const checked = gridRef.current?.rows.filter((r) => r.isSelected).map((o) => o.dataItem);

    if (checked.length > 0) {
      setCheckedItems((prev) => {
        return _.uniqBy([...prev, ...checked], 'pakBoxId');
      });
    }
  };

  const handleDelRow = () => {
    const checked = selectedGridRef.current?.rows
      .filter((r) => r.isSelected)
      .map((o) => o.dataItem);

    if (checked.length > 0) {
      setCheckedItems((prev) =>
        prev.filter((o) => !checked.some((x) => x.pakBoxId === o.pakBoxId))
      );
    }
  };

  const handleConfirm = () => {
    callbackFn(checkedItems);
    close();
  };

  const handleSearch = () => {
    findPrjTargetEquipmentBox(condition)
      .then((result: ItemList[]) => {
        if (result !== null) {
          setRowData(result.map((o) => ({ ...o })));
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const onInitialized2 = (grid) => {
    selectedGridRef.current = grid;
  };

  useEffect(() => {
    initCodes();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.적용', '적용')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('setup.label.반입 대상 Box 관리', '반입 대상 Box 관리'))}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="popup">
              <SearchCols>
                <label>{t('com.label.Box No', 'Box No')}</label>
                <CustomInputWithSearch
                  name="pakBoxNo"
                  value={condition?.pakBoxNo}
                  onChange={(e) => handleOnChange('pakBoxNo', e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.Box명', 'Box명')}</label>
                <CustomInputWithSearch
                  name="pakBoxNm"
                  value={condition?.pakBoxNm}
                  onChange={(e) => handleOnChange('pakBoxNm', e.target.value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={{
              pakBoxNo: '',
              pakBoxNm: '',
            }}
            setReset={setCondition}
            onSearch={handleSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <HalfContetntLayout style={{ marginTop: '24px' }}>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.Box 목록', 'Box 목록')}</h3>
              <span className="total">
                {t('com.label.총', '총')}
                <span>{rowData?.length ?? 0}</span>
                {t('com.label.건', '건')}
              </span>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="BoxGrid"
            layoutDefinition={layoutDefinition}
            rowData={rowData}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={onInitialized}
          />
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <span className="total">
                {t('com.label.총', '총')}
                <span>{checkedItems?.length ?? 0}</span>
                {t('com.label.건', '건')}
              </span>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.label.삭제', '삭제')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="selectedBoxGrid"
            layoutDefinition={layoutDefinition}
            rowData={checkedItems}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={onInitialized2}
          />
        </div>
      </HalfContetntLayout>
    </CustomDialog>
  );
};

export default PrjScheduleBoxPopup;
